import React, { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { VerisureQrCode } from './VerisureQrCode';
import { toast } from 'react-hot-toast';

export interface PrintableQrItem {
  id: string;
  tagId: string;
  qrUrl: string;
  title?: string;
  subtitle?: string;
  category?: string;
  outletName?: string;
  status?: string;
  isUsed?: boolean;
}

export interface BulkQrPrintStationModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: PrintableQrItem[];
  defaultTitle?: string;
  onDeleteItems?: (itemIds: string[]) => Promise<void> | void;
}

export const BulkQrPrintStationModal: React.FC<BulkQrPrintStationModalProps> = ({
  isOpen,
  onClose,
  items,
  defaultTitle = 'VERISURE BULK QR PRINT STATION',
  onDeleteItems,
}) => {
  const printContainerRef = useRef<HTMLDivElement>(null);
  const [currentItems, setCurrentItems] = useState<PrintableQrItem[]>(items);

  // Settings
  const [material, setMaterial] = useState<'transparent' | 'white'>('transparent');
  const [inkColor, setInkColor] = useState<'#000000' | '#ffffff' | '#ff5a00'>('#000000');
  const [layout, setLayout] = useState<'12_per_page' | '24_per_page' | '6_per_page'>('12_per_page');
  const [showCutGuides, setShowCutGuides] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>(() => items.map((i) => i.id));
  const [statusFilter, setStatusFilter] = useState<'all' | 'unclaimed' | 'used'>('all');
  const [showPostPrintPrompt, setShowPostPrintPrompt] = useState(false);
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState<'used' | 'selected' | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setCurrentItems(items);
    setSelectedIds(items.map((i) => i.id));
  }, [items]);

  // Toggle body class when modal is open to isolate print styling
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('bulk-qr-modal-active');
    } else {
      document.body.classList.remove('bulk-qr-modal-active');
    }
    return () => {
      document.body.classList.remove('bulk-qr-modal-active');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isItemUsed = (item: PrintableQrItem) =>
    item.isUsed ||
    item.status === 'registered' ||
    (!!item.title && item.title !== 'Unassigned Appliance Tag' && !item.title.startsWith('Blank'));

  const usedItems = currentItems.filter(isItemUsed);
  const blankItems = currentItems.filter((i) => !isItemUsed(i));

  const displayedItems = currentItems.filter((i) => {
    if (statusFilter === 'used') return isItemUsed(i);
    if (statusFilter === 'unclaimed') return !isItemUsed(i);
    return true;
  });

  // Toggle selection
  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedIds(displayedItems.map((i) => i.id));
  };

  const handleDeselectAll = () => {
    setSelectedIds([]);
  };

  const handleSelectUsedOnly = () => {
    setSelectedIds(usedItems.map((i) => i.id));
  };

  const itemsToPrint = displayedItems.filter((i) => selectedIds.includes(i.id));

  const handlePrintNow = () => {
    if (itemsToPrint.length === 0) {
      toast.error('Please select at least one QR code to print.');
      return;
    }
    document.body.classList.add('bulk-qr-modal-active');
    const cleanup = () => {
      setShowPostPrintPrompt(true);
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(() => {
      setShowPostPrintPrompt(true);
    }, 1500);
  };

  const handleSavePdf = () => {
    if (itemsToPrint.length === 0) {
      toast.error('Please select at least one QR code to export.');
      return;
    }
    const originalTitle = document.title;
    document.title = `VERISURE-Appliance-QR-Stickers-${material === 'transparent' ? 'Transparent' : 'Paper'}-${itemsToPrint.length}tags`;
    toast.success('In the print dialog, select "Save as PDF" as the destination printer to download your PDF file.', { duration: 5000 });
    document.body.classList.add('bulk-qr-modal-active');
    const cleanup = () => {
      document.title = originalTitle;
      setShowPostPrintPrompt(true);
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
      setShowPostPrintPrompt(true);
    }, 2500);
  };

  // Dimensions based on layout
  const layoutConfig = {
    '6_per_page': {
      gridCols: 'grid-cols-2 md:grid-cols-3',
      qrSize: 115,
      badgePadding: 'p-4',
      fontSizeTag: 'text-sm',
    },
    '12_per_page': {
      gridCols: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
      qrSize: 85,
      badgePadding: 'p-3',
      fontSizeTag: 'text-xs',
    },
    '24_per_page': {
      gridCols: 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6',
      qrSize: 58,
      badgePadding: 'p-2',
      fontSizeTag: 'text-[10px]',
    },
  }[layout];

  // Printable ink color (guarantee high contrast on white paper/PDF)
  const printableInkColor = inkColor === '#ffffff' ? '#000000' : inkColor;

  return (
    <div className="bulk-qr-modal-overlay fixed inset-0 z-[110] bg-slate-950/90 backdrop-blur-md flex flex-col overflow-y-auto print:overflow-visible print:bg-white print:p-0">
      {/* Print Specific CSS */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 8mm 6mm;
          }
          html, body {
            background: #ffffff !important;
            color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            overflow: visible !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Completely hide all background application content, navbar, tickers, footers */
          header, nav, footer, .no-print, .print-hide,
          body.bulk-qr-modal-active > #root > div > *:not(.bulk-qr-modal-overlay) {
            display: none !important;
          }
          .bulk-qr-modal-overlay {
            position: static !important;
            inset: auto !important;
            overflow: visible !important;
            background: #ffffff !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
            height: auto !important;
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .bulk-print-modal-container {
            position: static !important;
            display: block !important;
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
            background: #ffffff !important;
          }
          .printable-sheet {
            background: #ffffff !important;
            box-shadow: none !important;
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
            width: 100% !important;
            min-height: 0 !important;
            color: #000000 !important;
          }
          /* Force visibility of all sheet and sticker elements */
          .printable-sheet,
          .printable-sheet *,
          .sticker-item,
          .sticker-item * {
            visibility: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .sticker-item {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            background: #ffffff !important;
            box-shadow: none !important;
            color: ${printableInkColor} !important;
            border: ${
              showCutGuides
                ? '1px dashed #94a3b8'
                : '1px dashed transparent'
            } !important;
          }
          .sticker-item svg {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .sticker-item img {
            max-height: 24px !important;
            width: auto !important;
            display: block !important;
            margin: 0 auto !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Strict A4 Print Grid Columns */
          .print-grid-6_per_page {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 6mm !important;
          }
          .print-grid-12_per_page {
            display: grid !important;
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            gap: 4mm !important;
          }
          .print-grid-24_per_page {
            display: grid !important;
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
            gap: 3mm !important;
          }
        }
      `}</style>

      {/* Top Navbar for Screen Mode */}
      <div className="no-print sticky top-0 z-20 bg-slate-900/95 border-b border-slate-800 px-3 sm:px-6 py-2.5 sm:py-4 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-orange-500/10 border border-orange-500/20 text-orange-400 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <h2 className="text-sm sm:text-base font-black text-white truncate max-w-[200px] sm:max-w-none">{defaultTitle}</h2>
              <span className="hidden sm:inline-block px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-orange-500/20 text-orange-400 border border-orange-500/30">
                Master / Super Admin
              </span>
            </div>
            <p className="text-[11px] sm:text-xs text-slate-400">
              Bulk batch &bull; {itemsToPrint.length} of {items.length} selected
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1.5 sm:gap-2.5 overflow-x-auto max-w-full pb-1 sm:pb-0">
          {usedItems.length > 0 && (
            <button
              type="button"
              onClick={() => setDeleteConfirmTarget('used')}
              className="px-2.5 sm:px-3.5 py-2 sm:py-2.5 rounded-xl bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/30 text-[11px] sm:text-xs font-bold transition flex items-center gap-1 sm:gap-1.5 shrink-0"
              title="Bulk delete all used / claimed QR codes from station"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>Delete Used ({usedItems.length})</span>
            </button>
          )}

          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={() => setDeleteConfirmTarget('selected')}
              className="px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-xl bg-slate-800 hover:bg-red-950/40 text-slate-300 hover:text-red-300 border border-slate-700 hover:border-red-500/40 text-[11px] sm:text-xs font-bold transition flex items-center gap-1 shrink-0"
              title="Delete currently selected QR codes from print station"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>Selected ({selectedIds.length})</span>
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className="px-2.5 sm:px-3.5 py-2 sm:py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] sm:text-xs font-bold transition shrink-0"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSavePdf}
            disabled={itemsToPrint.length === 0}
            className="px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-[11px] sm:text-xs font-bold transition flex items-center gap-1 sm:gap-1.5 disabled:opacity-40 shrink-0"
            title="Save entire sheet as PDF"
          >
            <svg className="w-3.5 h-3.5 text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="12" y1="18" x2="12" y2="12"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
            <span className="hidden sm:inline">Save as PDF</span>
            <span className="sm:hidden">PDF</span>
          </button>
          <button
            type="button"
            onClick={handlePrintNow}
            disabled={itemsToPrint.length === 0}
            className="px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white text-[11px] sm:text-xs font-extrabold shadow-lg shadow-orange-500/20 transition flex items-center gap-1.5 disabled:opacity-40 shrink-0"
            title="Print to Bluetooth phone printer, AirPrint, or PC printer"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            <span>Print ({itemsToPrint.length})</span>
          </button>
        </div>
      </div>

      {/* Main Settings & Sheet Preview */}
      <div className="bulk-print-modal-container max-w-7xl w-full mx-auto p-4 md:p-8 flex-1 flex flex-col gap-5">
        {/* Post-Print Session Cleanup Banner */}
        {showPostPrintPrompt && (
          <div className="no-print bg-gradient-to-r from-amber-500/15 via-orange-500/15 to-red-500/15 border border-amber-500/30 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-amber-200 shadow-xl">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold text-base shrink-0 border border-amber-500/30">
                🖨️
              </span>
              <div>
                <span className="font-extrabold text-white text-sm">Print Session Completed!</span>{' '}
                <div className="text-slate-300 text-xs mt-0.5">
                  Finished labeling? You can bulk delete used/claimed QR codes to keep your print queue and station clear.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
              {usedItems.length > 0 && (
                <button
                  type="button"
                  onClick={() => setDeleteConfirmTarget('used')}
                  className="px-3.5 py-2 bg-red-600 hover:bg-red-500 text-white font-extrabold rounded-xl text-xs transition shadow-md shadow-red-600/25 flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Delete Used ({usedItems.length})</span>
                </button>
              )}
              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setDeleteConfirmTarget('selected')}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl text-xs transition flex items-center gap-1.5 border border-slate-700"
                >
                  <span>Delete Selected ({selectedIds.length})</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowPostPrintPrompt(false)}
                className="px-3 py-2 text-slate-400 hover:text-white font-semibold rounded-xl text-xs transition"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        {/* Phone Bluetooth & PC Universal Printing Instructions Banner */}
        <div className="no-print bg-[#0c101a] border border-white/[0.08] rounded-2xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-slate-300">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-xl bg-orange-500/10 border border-orange-500/25 text-orange-400 flex items-center justify-center font-bold shrink-0">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6.5 6.5l11 11L12 23V1l5.5 5.5-11 11"/>
              </svg>
            </span>
            <div>
              <span className="font-bold text-white">Phone (Bluetooth) & PC Universal Ready:</span>{' '}
              Print directly to mobile Bluetooth thermal printers (Zebra, Brother, Munbyn, Niimbot, RawBT), AirPrint (iOS), or standard office/vinyl printers on PC.
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-orange-400 font-medium whitespace-nowrap bg-orange-500/10 px-3 py-1 rounded-full border border-orange-500/20">
            <span>Destination: Printer or "Save as PDF"</span>
          </div>
        </div>

        {/* Settings Bar */}
        <div className="no-print bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            {/* 1. Material Background */}
            <div>
              <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-orange-400"></span>
                Sticker Material / Background
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setMaterial('transparent');
                    if (inkColor === '#ffffff') setInkColor('#000000');
                  }}
                  className={`py-2 px-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 border ${
                    material === 'transparent'
                      ? 'bg-orange-500/20 text-orange-400 border-orange-500'
                      : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-white'
                  }`}
                >
                  <span className="w-3 h-3 rounded-full border border-dashed border-orange-400 flex items-center justify-center text-[7px]">
                    Ø
                  </span>
                  <span>Transparent Vinyl</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMaterial('white')}
                  className={`py-2 px-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 border ${
                    material === 'white'
                      ? 'bg-orange-500/20 text-orange-400 border-orange-500'
                      : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-white'
                  }`}
                >
                  <span className="w-3 h-3 rounded-full bg-white border border-slate-300"></span>
                  <span>Opaque White Paper</span>
                </button>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                {material === 'transparent'
                  ? 'Zero background fill. Perfect for appliances (blends smoothly with chassis).'
                  : 'Crisp white solid card background.'}
              </p>
            </div>

            {/* 2. Ink / Contrast Color */}
            <div>
              <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-2">
                QR Code & Text Ink
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setInkColor('#000000')}
                  className={`py-2 px-1 rounded-xl text-xs font-bold transition border flex items-center justify-center gap-1 ${
                    inkColor === '#000000'
                      ? 'bg-slate-800 text-white border-white'
                      : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-white'
                  }`}
                  title="Best for silver, white, chrome, or light-colored appliances"
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-black border border-white/50"></span>
                  <span>Black Ink</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInkColor('#ff5a00')}
                  className={`py-2 px-1 rounded-xl text-xs font-bold transition border flex items-center justify-center gap-1 ${
                    inkColor === '#ff5a00'
                      ? 'bg-orange-500/20 text-orange-400 border-orange-500'
                      : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-white'
                  }`}
                  title="Verisure brand orange ink"
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ff5a00]"></span>
                  <span>Verisure</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInkColor('#ffffff')}
                  className={`py-2 px-1 rounded-xl text-xs font-bold transition border flex items-center justify-center gap-1 ${
                    inkColor === '#ffffff'
                      ? 'bg-slate-800 text-white border-white'
                      : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-white'
                  }`}
                  title="Best for black/dark appliances (e.g. black microwave, dark inverters)"
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-white"></span>
                  <span>White Ink</span>
                </button>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                {inkColor === '#ffffff'
                  ? 'Recommended when sticking transparent vinyl on black appliances.'
                  : 'Recommended for silver, steel, and white appliances.'}
              </p>
            </div>

            {/* 3. Grid Sheet Layout */}
            <div>
              <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-2">
                Stickers Per Sheet
              </label>
              <select
                value={layout}
                onChange={(e: any) => setLayout(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-orange-500"
              >
                <option value="12_per_page">12 Stickers / Sheet (Standard Appliance Scale)</option>
                <option value="24_per_page">24 Stickers / Sheet (Compact / Electronics Scale)</option>
                <option value="6_per_page">6 Stickers / Sheet (Large / Industrial Scale)</option>
              </select>
              <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showCutGuides}
                    onChange={(e) => setShowCutGuides(e.target.checked)}
                    className="rounded text-orange-500 focus:ring-orange-500 bg-slate-900 border-slate-700"
                  />
                  <span>Cut Guides</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showInstructions}
                    onChange={(e) => setShowInstructions(e.target.checked)}
                    className="rounded text-orange-500 focus:ring-orange-500 bg-slate-900 border-slate-700"
                  />
                  <span>Appliance Guide</span>
                </label>
              </div>
            </div>

            {/* 4. Selection Quick Tools & Filter */}
            <div className="flex flex-col justify-end">
              <div className="text-[11px] font-bold text-slate-400 mb-2 flex justify-between items-center">
                <span>Selected: {itemsToPrint.length}/{displayedItems.length}</span>
                <div className="flex gap-2 text-orange-400">
                  <button type="button" onClick={handleSelectAll} className="hover:underline">
                    All
                  </button>
                  <span>&bull;</span>
                  {usedItems.length > 0 && (
                    <>
                      <button type="button" onClick={handleSelectUsedOnly} className="text-red-400 hover:underline">
                        Used ({usedItems.length})
                      </button>
                      <span>&bull;</span>
                    </>
                  )}
                  <button type="button" onClick={handleDeselectAll} className="hover:underline">
                    None
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={statusFilter}
                  onChange={(e: any) => setStatusFilter(e.target.value)}
                  className="bg-slate-950 border border-slate-700 text-white rounded-xl px-2.5 py-1.5 text-xs focus:outline-none focus:border-orange-500"
                >
                  <option value="all">All ({currentItems.length})</option>
                  <option value="unclaimed">Blank Only ({blankItems.length})</option>
                  <option value="used">Used Only ({usedItems.length})</option>
                </select>
                <div className="bg-slate-950 border border-slate-800 rounded-xl p-1.5 flex items-center justify-between text-xs">
                  <span className="text-slate-400 text-[10px]">Target:</span>
                  <span className="font-mono text-orange-400 font-bold text-[10px]">
                    {material === 'transparent' ? 'Clear Vinyl' : 'White A4'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Live Printable Sheet */}
        <div
          ref={printContainerRef}
          className={`printable-sheet rounded-3xl p-6 md:p-8 shadow-2xl min-h-[600px] border transition-all ${
            material === 'transparent'
              ? 'bg-[radial-gradient(#334155_1px,transparent_1px)] [background-size:16px_16px] bg-slate-950/80 border-slate-800 text-white print:bg-white print:border-none print:text-black'
              : 'bg-white text-slate-950 border-slate-200'
          }`}
        >
          {/* Header banner on sheet */}
          <div
            className={`border-b pb-3 mb-6 flex flex-wrap justify-between items-center text-xs ${
              material === 'transparent' ? 'border-slate-800 text-slate-400 print:border-slate-300 print:text-slate-800' : 'border-slate-200 text-slate-600'
            }`}
          >
            <div className="font-bold flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
              <span className="print:text-black">VERISURE APPLIANCE IDENTIFICATION &bull; OFFICIAL PRINT BATCH</span>
            </div>
            <div className="font-mono text-[11px] print:text-slate-700">
              Material: {material === 'transparent' ? 'TRANSPARENT ADHESIVE VINYL' : 'WHITE OPAQUE PAPER'} &bull;{' '}
              {itemsToPrint.length} Stickers Ready
            </div>
          </div>

          {/* Grid of Stickers */}
          <div className={`grid gap-4 ${layoutConfig.gridCols} print-grid-${layout}`}>
            {itemsToPrint.map((item) => (
              <div
                key={item.id || item.tagId}
                onClick={() => handleToggleSelect(item.id)}
                className={`sticker-item rounded-2xl ${layoutConfig.badgePadding} flex flex-col items-center justify-between text-center relative transition select-none cursor-pointer group ${
                  material === 'transparent'
                    ? 'border border-dashed border-white/20 bg-white/[0.04] hover:border-orange-500/50 text-slate-100 print:bg-white print:text-black'
                    : 'border border-dashed border-slate-300 bg-white hover:border-orange-500/50 shadow-sm text-slate-900 print:bg-white print:text-black'
                }`}
                style={{
                  color: inkColor === '#ffffff' ? undefined : inkColor,
                }}
              >
                {/* Deselect checkbox for screen */}
                <div className="no-print absolute top-2 right-2 opacity-60 group-hover:opacity-100">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={() => handleToggleSelect(item.id)}
                    className="rounded text-orange-500 focus:ring-orange-500"
                  />
                </div>

                {/* Sticker Header */}
                <div className="w-full flex items-center justify-between border-b pb-1 mb-1 text-[8px] font-extrabold uppercase tracking-wider opacity-85 print:opacity-100 print:border-slate-300">
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block"></span>
                    VERISURE
                  </span>
                  <div className="flex items-center gap-1">
                    {isItemUsed(item) && (
                      <span className="no-print px-1 py-0.2 rounded text-[7px] font-black bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                        USED
                      </span>
                    )}
                    <span className="font-mono">{item.category?.replace('_', ' ') || 'APPLIANCE'}</span>
                  </div>
                </div>

                {/* Official Verisure Logo */}
                <div className="flex items-center justify-center my-0.5">
                  <img
                    src="/official.png"
                    alt="Verisure Logo"
                    className={`${layout === '24_per_page' ? 'h-3.5' : layout === '12_per_page' ? 'h-5' : 'h-6'} w-auto object-contain`}
                  />
                </div>

                {/* Inscription: "I am Verisure" */}
                <div className="text-center my-0.5">
                  <div className={`font-black tracking-tight ${layout === '24_per_page' ? 'text-[8px]' : layout === '12_per_page' ? 'text-[9.5px]' : 'text-[11px]'} leading-tight print:text-black`}>
                    I am <span className="text-[#ff5a00] print:text-[#ff5a00] font-black">Verisure</span>
                  </div>
                  <div className="w-6 h-0.5 bg-[#ff5a00] mx-auto mt-0.5 rounded-full print:bg-[#ff5a00]"></div>
                </div>

                {/* The QR Code with TRANSPARENT background support */}
                <div
                  className={`p-1.5 rounded-xl my-1 flex items-center justify-center transition-all ${
                    material === 'transparent'
                      ? 'bg-transparent'
                      : 'bg-white border border-slate-200 shadow-inner print:border-none print:shadow-none'
                  }`}
                >
                  <VerisureQrCode
                    value={item.qrUrl}
                    size={layoutConfig.qrSize}
                    level="H"
                    bgColor="transparent"
                    fgColor={printableInkColor}
                    includeMargin={false}
                    showCenterBadge={true}
                    badgeTheme={material === 'transparent' && printableInkColor !== '#000000' ? 'dark' : 'light'}
                  />
                </div>

                {/* Hardware Identity & Tag ID */}
                <div className="my-1 w-full">
                  <div className={`font-mono font-black tracking-wider ${layoutConfig.fontSizeTag} print:text-black`}>
                    {item.tagId}
                  </div>
                  {item.title && (
                    <div className="text-[9px] font-bold truncate max-w-full opacity-90 print:opacity-100 print:text-slate-800">
                      {item.title}
                    </div>
                  )}
                  {item.outletName && (
                    <div className="text-[7.5px] truncate max-w-full opacity-60 print:opacity-90 print:text-slate-600">
                      Outlet: {item.outletName}
                    </div>
                  )}
                </div>

                {/* Appliance Application Instructions */}
                {showInstructions && (
                  <div
                    className={`w-full rounded p-1 text-[7px] leading-tight mt-1 text-left ${
                      material === 'transparent'
                        ? 'border border-white/15 bg-white/[0.03] text-slate-300 print:border-slate-300 print:bg-slate-50 print:text-slate-800'
                        : 'border border-slate-200 bg-slate-50 text-slate-700'
                    }`}
                  >
                    <span className="font-bold text-orange-500">Appliance Tag:</span> Peel & stick firmly to clean surface. Scan to view ledger & log certified repairs.
                  </div>
                )}
              </div>
            ))}
          </div>

          {itemsToPrint.length === 0 && (
            <div className="py-20 text-center text-slate-400">
              <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <p className="text-sm font-semibold">No QR codes selected for printing.</p>
              <button
                type="button"
                onClick={handleSelectAll}
                className="mt-2 text-xs text-orange-400 font-bold hover:underline"
              >
                Click here to select all {displayedItems.length} stickers
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Safe Confirmation Modal for Bulk Delete */}
      {deleteConfirmTarget && (
        <div className="fixed inset-0 z-[130] bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-lg w-full p-6 space-y-4 shadow-2xl relative">
            <div className="w-12 h-12 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-500 flex items-center justify-center text-xl mx-auto">
              🗑️
            </div>
            <div className="text-center">
              <h3 className="text-lg font-black text-white">
                {deleteConfirmTarget === 'used'
                  ? `Bulk Delete ${usedItems.length} Used QR Code(s)?`
                  : `Bulk Delete ${selectedIds.length} Selected QR Code(s)?`}
              </h3>
              <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto leading-relaxed">
                This will permanently delete these QR codes from the print station queue and registry. This action cannot be undone.
              </p>
            </div>

            {/* List preview of codes to delete */}
            <div className="max-h-48 overflow-y-auto bg-slate-950 border border-slate-800 rounded-2xl p-3 divide-y divide-slate-800/60 text-xs">
              {(deleteConfirmTarget === 'used' ? usedItems : itemsToPrint).map((target) => (
                <div key={target.id} className="py-2 flex items-center justify-between gap-2">
                  <div>
                    <div className="font-mono font-bold text-white text-[11px]">{target.tagId}</div>
                    <div className="text-[10px] text-slate-400 truncate max-w-[240px]">
                      {target.title || target.outletName || 'Appliance Tag'}
                    </div>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                      isItemUsed(target)
                        ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                        : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                    }`}
                  >
                    {isItemUsed(target) ? 'Used / Bound' : 'Blank Tag'}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmTarget(null)}
                disabled={isDeleting}
                className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl text-xs transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={async () => {
                  setIsDeleting(true);
                  const targetsToDelete = deleteConfirmTarget === 'used' ? usedItems : itemsToPrint;
                  const idsToDelete = targetsToDelete.map((i) => i.id || i.tagId);
                  try {
                    if (onDeleteItems) {
                      await onDeleteItems(idsToDelete);
                    }
                    const remaining = currentItems.filter(
                      (i) => !idsToDelete.includes(i.id) && !idsToDelete.includes(i.tagId)
                    );
                    setCurrentItems(remaining);
                    setSelectedIds((prev) => prev.filter((id) => !idsToDelete.includes(id)));
                    toast.success(`Successfully deleted ${idsToDelete.length} QR code(s) from print station!`);
                    setDeleteConfirmTarget(null);
                    setShowPostPrintPrompt(false);
                  } catch (err: any) {
                    toast.error('Failed to delete QR codes: ' + err.message);
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white font-extrabold rounded-xl text-xs transition shadow-lg shadow-red-600/25 flex items-center justify-center gap-1.5"
              >
                {isDeleting ? 'Deleting...' : `Confirm Bulk Delete (${deleteConfirmTarget === 'used' ? usedItems.length : selectedIds.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
